import { FC } from "preact/compat";
import { useComputed } from "@preact/signals";
import { BudgetFeeLine, useApp } from "~/core";
import { UserAvatar } from "~/components";
import LinesStep from "./LinesStep";
import { CreateStepProps, PhaseLines } from "./interfaces";
import useLineSelection from "./useLineSelection";
import { getFeeLines, isFeeLineUsed } from "./utils";

const Tasks: FC<CreateStepProps> = ({
    budget,
    loading,
    hasNext,
    onClose,
    onNext,
}) => {
    const { api, snack } = useApp();
    const selection = useLineSelection();
    const usedCount = useComputed(
        () =>
            getFeeLines(budget).filter((line) =>
                isFeeLineUsed(api, budget, line),
            ).length,
    );
    const phases = useComputed(() =>
        budget.phases.value
            .map(
                (phase): PhaseLines<BudgetFeeLine> => ({
                    phase,
                    lines: phase.lines.value.filter(
                        (line): line is BudgetFeeLine =>
                            line.t === "A" && !isFeeLineUsed(api, budget, line),
                    ),
                }),
            )
            .filter((item) => item.lines.length > 0),
    );

    const handleCreate = async () => {
        const selected = selection.getSelected(phases.peek());

        if (selected.length === 0) {
            onNext();
            return;
        }

        const selectedIds = selected.map((line) => line.id);

        loading.value = true;

        try {
            // used & unselected lines are excluded alike
            const [successCount, failCount] = await api.task.fromBudget(
                budget,
                budget.project,
                {
                    phases: [],
                    lines: getFeeLines(budget)
                        .filter((line) => !selectedIds.includes(line.id))
                        .map((line) => line.id),
                },
            );

            if (successCount) {
                snack.info(
                    "OK",
                    `Created ${successCount} task(s) from the budget`,
                );
            }

            if (failCount) {
                snack.error(
                    "Failed to create task(s)",
                    `${failCount} task(s) could not be created`,
                );
            }

            onNext();
        } finally {
            loading.value = false;
        }
    };

    const createLabel =
        phases.value.length === 0
            ? hasNext
                ? "Continue"
                : "Done"
            : hasNext
              ? "Create tasks & continue"
              : "Create tasks";

    return (
        <LinesStep
            title="Select tasks to create"
            emptyText="This budget has no fee lines to create tasks from"
            phases={phases.value}
            usedCount={usedCount.value}
            selection={selection}
            loading={loading.value}
            createLabel={createLabel}
            onClose={onClose}
            onCreate={handleCreate}
            renderMeta={(line) => (
                <div className="flex items-center gap-x-2">
                    <UserAvatar
                        user={api.employee.getAndSub(line.data.employee)}
                    />
                    <span className="shrink-0 text-gray-500">
                        {line.data.hours.toNumber()}hr
                    </span>
                </div>
            )}
        />
    );
};

export default Tasks;
